import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPorAsins } from "../porAsinList/porAsinListSlice";
import { RootState } from "../app/rootReducer";
import {
  Table,
  CollectionPreferences,
  Pagination,
  Header,
} from "@amzn/awsui-components-react/polaris";
import { pageSelectorOptions } from "../porAsinPageControl/porAsinPageControl";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { WEBSITE_TITLE } from "../../constants/common";
import { SortFacet } from "../../server/asin-central/search";
import {
  asinTableColumnDefinitions,
  VALIDATION_COLUMN_DEFINITIONS,
} from "./asinValidationTable-config";
import {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  setValidationPageNumber,
} from "./asinValidationTableConnector";
import {
  defaultSort,
  TableHeaderMessage,
  EmptyTableState,
  ValidationActionButtons,
  ValidationEditButtons,
} from "../porAsinList/TableCommonElements";
import { getEditValidationColumnDefs } from "./validationListEdit-config";
import { writeToLocalStorage } from "../../util/localStorageUtils";
import { trackComponentView } from "../telemetry/telemetry";
import { ValidationModal } from "./validationAttributeModal";

export const AsinValidationTable = () => {
  const dispatch = useDispatch();
  const {
    currentPagePorAsins,
    isLoading,
    count,
    isGettingEditableAttributes,
    editableAttributes,
  } = useSelector((state: RootState) => state.porAsinList);
  const {
    isEditing,
    editableValidationAsins,
    preferences,
    sort,
    descending,
    pageNumber,
    selectedValidationService,
    refreshFlipper,
  } = useSelector((state: RootState) => state.asinValidationTableControl);
  const {
    queryString,
    activePropertyFilters,
    activePropertyFiltersVisibilityEnabled,
  } = useSelector((state: RootState) => state.generateQuery);

  const getSort = (): SortFacet[] =>
    sort === undefined
      ? defaultSort
      : [
          {
            attributeName: sort?.sortingField,
            ascending: !descending,
          },
        ];

  useEffect(() => {
    trackComponentView("validationTable", "table");
  }, []);

  useEffect(() => {
    if (queryString) {
      const offset = preferences.pageSize * (pageNumber - 1);
      dispatch(
        fetchPorAsins(
          offset,
          preferences.pageSize,
          queryString,
          getSort(),
          false
        )
      );
    }
  }, [
    pageNumber,
    queryString,
    preferences.pageSize,
    dispatch,
    sort,
    descending,
    refreshFlipper,
  ]);

  useEffect(() => {
    if (activePropertyFiltersVisibilityEnabled) {
      dispatch(
        mergeVisibleContentPreferences({
          activeColumns: Object.keys(activePropertyFilters ?? {}),
        })
      );
    }
  }, [queryString, activePropertyFiltersVisibilityEnabled]);

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });

  return (
    <div data-testid="asin-validation-table-wrapper">
      <ValidationModal />
      <Table
        data-testid="asin-validation-table"
        header={
          <Header
            data-testid="asin-validation-header"
            variant="h1"
            description={TableHeaderMessage(
              (pageNumber - 1) * preferences.pageSize,
              currentPagePorAsins.length,
              count
            )}
            actions={
              isEditing ? (
                <ValidationEditButtons />
              ) : (
                <ValidationActionButtons totalCount={count} />
              )
            }
          >
            {WEBSITE_TITLE}
          </Header>
        }
        loading={isLoading}
        loadingText={"Loading"}
        empty={EmptyTableState()}
        columnDefinitions={
          isEditing
            ? getEditValidationColumnDefs(
                editableAttributes,
                selectedValidationService.overallAttributeName
              )
            : VALIDATION_COLUMN_DEFINITIONS
        }
        items={isEditing ? editableValidationAsins : currentPagePorAsins}
        stickyHeader={true}
        sortingColumn={sort}
        sortingDescending={descending}
        onSortingChange={(event) => dispatch(setSort(event.detail))}
        pagination={
          isEditing ? undefined : (
            <Pagination
              data-testid="asin-validation-pagination"
              pagesCount={Math.ceil(count / preferences.pageSize)}
              currentPageIndex={pageNumber}
              disabled={isLoading}
              onChange={({ detail }) => {
                dispatch(
                  setValidationPageNumber({
                    pageNumber: detail.currentPageIndex,
                  })
                );
              }}
            />
          )
        }
        visibleColumns={preferences.visibleContent}
        preferences={
          isEditing ? undefined : (
            <CollectionPreferences
              data-testid="asin-validation-preferences"
              onConfirm={(event) => {
                writeToLocalStorage(
                  "VALIDATION_TABLE_PREFERENCES",
                  JSON.stringify(event.detail)
                );
                dispatch(setPreferences(event.detail));
              }}
              title={"Preferences"}
              confirmLabel={"Confirm"}
              cancelLabel={"Cancel"}
              disabled={isLoading}
              preferences={preferences}
              pageSizePreference={pageSelectorOptions}
              visibleContentPreference={asinTableColumnDefinitions}
            />
          )
        }
        className={"porTable"}
      />
    </div>
  );
};
